import { secondsToMilliseconds } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { parseDateOnly } from "shared/src/utils/dateUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/sharp-regular-svg-icons";
import { useDisclosure } from "@mantine/hooks";
import classnames from "classnames";
import Navbar from "@/components/layout/Navbar";
import AddDocumentButton from "@/components/UploadAdditionalDocuments/AddDocumentButton.tsx";
import {
  Appeal,
  AppealSupportingDocument,
  AppealSupportingDocumentType,
  GetSupportingDocumentsForAppealQuery,
  GetSupportingDocumentsForAppealQueryVariables,
} from "@/__generated__/graphql.ts";
import { DocumentUploadModalMode } from "@/components/UploadAdditionalDocuments/types/modal.ts";
import { GET_SUPPORTING_DOCUMENTS_FOR_APPEAL } from "@/components/UploadAdditionalDocuments/graphql/requests.ts";
import { UploadedAdditionalDocument } from "@/components/UploadAdditionalDocuments/UploadedAdditionalDocument";
import { DocumentUploadModal } from "@/components/UploadAdditionalDocuments/modals/DocumentUploadModal";
import { ViewDocumentModal } from "@/components/UploadAdditionalDocuments/modals/ViewDocumentModal";
import { DeleteDocumentModal } from "@/components/UploadAdditionalDocuments/modals/DeleteDocumentModal";
import { FaqModal } from "@/components/UploadAdditionalDocuments/modals/FaqModal";
import Loader from "@/components/shared/Loader.tsx";
import { Alert } from "@/components/Alert";
import { isGlp1Condition } from "shared/src/data/medication-condition-pairs";

import "../../styles/components/landing-page.scss";
import styles from "./refer-patient-documents.module.scss";

const TEN_SECONDS = secondsToMilliseconds(10);

// Type for supporting documents without isIncludedInAppeal (provider users can see all documents)
type ProviderAppealSupportingDocument = Omit<
  AppealSupportingDocument,
  "isIncludedInAppeal"
>;

const getDocumentIndexInDocumentsOfSameType = (
  document: ProviderAppealSupportingDocument,
  documents: ProviderAppealSupportingDocument[],
) => {
  if (document.documentType === AppealSupportingDocumentType.Other) {
    const documentsWithSameName = documents.filter(
      (doc) =>
        doc.documentType === document.documentType &&
        doc.otherDocumentTypeName === document.otherDocumentTypeName,
    );

    return documentsWithSameName.findIndex(
      (doc) => doc.documentId === document.documentId,
    );
  }

  const sameTypeDocuments = documents.filter(
    (doc) => doc.documentType === document.documentType,
  );

  return sameTypeDocuments.findIndex(
    (doc) => doc.documentId === document.documentId,
  );
};

const getReferralDetails = (appeal?: Appeal) => {
  let dob = "";

  if (appeal?.patient?.dateOfBirth) {
    const date = parseDateOnly(appeal?.patient?.dateOfBirth);
    const formattedDate = new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }).format(date);
    dob = formattedDate;
  }

  return {
    name: `${appeal?.patient?.firstName ?? ""} ${appeal?.patient?.lastName ?? ""}`,
    dob,
    email: appeal?.referralPatientEmail,
    condition: appeal?.denial?.condition,
    medication: appeal?.denial?.medicationBrandName,
  };
};

const ReferPatientDocuments = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const appealId = searchParams.get("appealId") as string;

  if (!appealId) {
    navigate("/");
  }

  const [
    isDocumentUploadModalOpen,
    { open: openDocumentUploadModal, close: closeDocumentUploadModal },
  ] = useDisclosure();
  const [
    isViewDocumentModalOpen,
    { open: openViewDocumentModal, close: closeViewDocumentModal },
  ] = useDisclosure();
  const [
    isDeleteDocumentModalOpen,
    { open: openDeleteDocumentModal, close: closeDeleteDocumentModal },
  ] = useDisclosure();
  const [isFaqModalOpen, { open: openFaqModal, close: closeFaqModal }] =
    useDisclosure();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [supportingDocumentBeingViewed, setSupportingDocumentBeingViewed] =
    useState<ProviderAppealSupportingDocument | null>(null);
  const [documentUploadModalMode, setDocumentUploadModalMode] = useState(
    DocumentUploadModalMode.ADD,
  );
  const [initiallyOpenedQuestionFaqModal, setInitiallyOpenedQuestionFaqModal] =
    useState<string>();
  const [documentBeingEdited, setDocumentBeingEdited] =
    useState<ProviderAppealSupportingDocument | null>(null);
  const [supportingDocumentBeingDeleted, setSupportingDocumentBeingDeleted] =
    useState<ProviderAppealSupportingDocument | null>(null);
  const {
    data: supportingDocumentsData,
    loading: isRequestForSupportingDocumentsLoading,
    error: errorRequestingSupportingDocuments,
  } = useQuery<
    GetSupportingDocumentsForAppealQuery,
    GetSupportingDocumentsForAppealQueryVariables
  >(GET_SUPPORTING_DOCUMENTS_FOR_APPEAL, {
    variables: {
      appealId,
    },
  });
  const [pageError, setPageError] = useState<string | null>(null);

  useEffect(() => {
    if (successMessage) {
      const timeout = setTimeout(() => {
        setSuccessMessage(null);
      }, TEN_SECONDS);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [successMessage]);

  const supportingDocuments =
    supportingDocumentsData?.getAppealById?.supportingDocuments;
  const condition = supportingDocumentsData?.getAppealById?.denial?.condition;
  const hasUploadedALetterOfMedicalNecessity = Boolean(
    supportingDocuments?.some(
      (document) =>
        document.documentType ===
        AppealSupportingDocumentType.LetterOfMedicalNecessity,
    ),
  );

  const referralDetails = getReferralDetails(
    supportingDocumentsData?.getAppealById as Appeal,
  );

  const handleAddDocumentButtonClick = () => {
    setDocumentUploadModalMode(DocumentUploadModalMode.ADD);
    openDocumentUploadModal();
  };

  const handleAddLomnButtonClick = () => {
    setDocumentUploadModalMode(DocumentUploadModalMode.ADD_LOMN);
    openDocumentUploadModal();
  };

  const handleCloseDocumentUploadModal = useCallback(() => {
    closeDocumentUploadModal();
    setDocumentUploadModalMode(DocumentUploadModalMode.ADD);
    setDocumentBeingEdited(null);
  }, [closeDocumentUploadModal]);

  const handleSubmitDocument = () => {
    setSuccessMessage("Document added");
  };

  const handleOnViewDocument =
    (supportingDocument: ProviderAppealSupportingDocument) => () => {
      setSupportingDocumentBeingViewed(supportingDocument);
      openViewDocumentModal();
    };

  const handleOnEditDocument =
    (document: ProviderAppealSupportingDocument) => () => {
      setPageError(null);
      setDocumentUploadModalMode(DocumentUploadModalMode.EDIT);
      setDocumentBeingEdited(document);
      openDocumentUploadModal();
    };

  const handleOnDeleteDocument =
    (document: ProviderAppealSupportingDocument) => () => {
      setPageError(null);
      setSupportingDocumentBeingDeleted(document);
      openDeleteDocumentModal();
    };

  const handleLoadDocumentFilesError = useCallback(() => {
    setPageError(
      "There was an issue with loading the files of your document. Please try again later.",
    );
    handleCloseDocumentUploadModal();
  }, [handleCloseDocumentUploadModal]);

  useEffect(() => {
    if (errorRequestingSupportingDocuments) {
      setPageError(
        "There was an issue loading your documents. Please try again later.",
      );
    }
  }, [errorRequestingSupportingDocuments]);

  return (
    <Navbar titleText="Refer Patient">
      <div className={styles.pageContainer}>
        <div className={styles.formContainer}>
          <h2 className={styles.formHeader}>
            {
              "Are there additional documents you can attach to strengthen this appeal?"
            }
          </h2>
          <p className={styles.formDescription}>
            {
              "Adding additional documents is not required but can strengthen the patients appeal."
            }
            <button
              type="button"
              className={styles.whatShouldUpload}
              onClick={() => {
                if (isGlp1Condition(condition)) {
                  setInitiallyOpenedQuestionFaqModal(
                    "What documents are recommended for GLP-1 appeals?",
                  );
                }
                openFaqModal();
              }}
            >
              {"What should I upload?"}
            </button>
          </p>
          <hr className={styles.formHorizontalRule} />
          <div className={styles.uploadsContainer}>
            {pageError && (
              <Alert variant="error" title="An Error Occured">
                {pageError}
              </Alert>
            )}
            {!hasUploadedALetterOfMedicalNecessity && (
              <AddDocumentButton
                onClick={handleAddLomnButtonClick}
                description="A statement supporting the patient’s treatment is critical to their care. While not typically required, this can strengthen their case."
              >
                {"Doctor’s letter of medical necessity"}
              </AddDocumentButton>
            )}
            <AddDocumentButton
              onClick={handleAddDocumentButtonClick}
              description="Upload lab results, medical records, other supporting documents."
            >
              {"Add a document"}
            </AddDocumentButton>

            {isRequestForSupportingDocumentsLoading && (
              <div className={styles.supportDocsLoading}>
                <Loader color="loader_almost_black" width="36" height="36" />
              </div>
            )}
            {supportingDocuments &&
              Boolean(supportingDocuments.length) &&
              supportingDocuments.map((supportingDocument) => (
                <UploadedAdditionalDocument
                  key={supportingDocument.documentId}
                  type={
                    supportingDocument.documentType ===
                      AppealSupportingDocumentType.Other &&
                    supportingDocument.otherDocumentTypeName
                      ? supportingDocument.otherDocumentTypeName
                      : supportingDocument.documentType
                  }
                  index={getDocumentIndexInDocumentsOfSameType(
                    supportingDocument,
                    supportingDocuments,
                  )}
                  onView={handleOnViewDocument(supportingDocument)}
                  onEdit={handleOnEditDocument(supportingDocument)}
                  onDelete={handleOnDeleteDocument(supportingDocument)}
                />
              ))}
            <div className={styles.ctas}>
              <button
                type="submit"
                onClick={() =>
                  navigate("/", {
                    state: {
                      success: true,
                    },
                  })
                }
                disabled={isRequestForSupportingDocumentsLoading}
                className={classnames("btn", "dark_double_right_arrow", {
                  loading_button: isRequestForSupportingDocumentsLoading,
                })}
              >
                {isRequestForSupportingDocumentsLoading ? (
                  <div className="loading_indicator_container">
                    <div className="loading_indicator_text">{"Loading..."}</div>
                    <div className="loading_indicator">
                      <Loader
                        color="loader_light_volt"
                        width="36"
                        height="36"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    {"Done with documents"}
                    <span className="arrow" />
                  </>
                )}
              </button>

              <Link to="/">{"Continue without documents"}</Link>
            </div>
          </div>
          <DocumentUploadModal
            isOpen={isDocumentUploadModalOpen}
            onClose={handleCloseDocumentUploadModal}
            onSubmitDocument={handleSubmitDocument}
            mode={documentUploadModalMode}
            document={documentBeingEdited}
            onLoadingDocumentFilesError={handleLoadDocumentFilesError}
            appealId={appealId}
          />
          {supportingDocumentBeingViewed && (
            <ViewDocumentModal
              isOpen={isViewDocumentModalOpen}
              onClose={closeViewDocumentModal}
              files={supportingDocumentBeingViewed.files}
              documentType={supportingDocumentBeingViewed.documentType}
              otherDocumentTypeName={
                supportingDocumentBeingViewed.otherDocumentTypeName ?? undefined
              }
            />
          )}
          {supportingDocumentBeingDeleted && (
            <DeleteDocumentModal
              appealId={appealId}
              document={supportingDocumentBeingDeleted}
              isOpen={isDeleteDocumentModalOpen}
              onClose={closeDeleteDocumentModal}
              onDeleteConfirmation={() => {
                setSuccessMessage("Document deleted");
              }}
              onDeleteError={() =>
                setPageError(
                  "There was an issue deleting the document. Please try again later.",
                )
              }
            />
          )}
          <FaqModal
            initiallyOpenedQuestion={initiallyOpenedQuestionFaqModal}
            condition={condition}
            isOpen={isFaqModalOpen}
            onClose={() => {
              setInitiallyOpenedQuestionFaqModal("");
              closeFaqModal();
            }}
          />
        </div>
        <div className={styles.patientInfoContainer}>
          <div className={styles.patientInfoContainerInner}>
            <h3 className={styles.referralDetailsHeading}>
              {"Referral Details"}
            </h3>
            <div className={styles.patientData}>
              <div>
                <span>{"Patient name"}</span>
                {referralDetails.name}
              </div>
              <div>
                <span>{"DoB"}</span>
                {referralDetails.dob}
              </div>
              <div>
                <span>{"Email"}</span>
                {referralDetails.email}
              </div>
              <div>
                <span>{"Condition"}</span>
                {referralDetails.condition}
              </div>
              <div>
                <span>{"Medication"}</span>
                {referralDetails.medication}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.whyAttach}>
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => {
            setInitiallyOpenedQuestionFaqModal("Are documents required?");
            openFaqModal();
          }}
        >
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>{"Why attach documents?"}</span>
        </button>
      </div>
    </Navbar>
  );
};

export default ReferPatientDocuments;
